import React, { useState } from "react";
import "./HealthIndicatorWidget.css";
import { useHealthPoller } from "./useHealthPoller";
import { HealthDetailsModal } from "./HealthDetailsModal";

interface HealthIndicatorWidgetProps {
  isAdmin?: boolean;
  enabled?: boolean;
  pollingInterval?: number;
}

export const HealthIndicatorWidget: React.FC<HealthIndicatorWidgetProps> = ({
  isAdmin = true,
  enabled = true,
  pollingInterval = 30000,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { health, isLoading, manualRefresh } = useHealthPoller({
    enabled: enabled && isAdmin,
    pollingInterval,
  });

  // Don't render if user is not admin
  if (!isAdmin) {
    return null;
  }

  const getStatusColor = (): string => {
    if (isLoading && !health) return "#fbbf24"; // amber while loading
    if (!health || health.status === "unreachable") return "#6b7280"; // gray
    if (health.status === "unhealthy") return "#ef4444"; // red
    return "#10b981"; // green
  };

  const getStatusTooltip = (): string => {
    if (isLoading && !health) return "Checking...";
    if (!health) return "No data yet";
    if (health.status === "healthy") return "News Processor is healthy";
    if (health.status === "unhealthy") return "News Processor is unhealthy";
    return "News Processor is unreachable";
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await manualRefresh();
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <>
      <div className="health-indicator-container">
        <button
          className="health-indicator-button"
          onClick={() => setIsModalOpen(true)}
          title={getStatusTooltip()}
          aria-label="News Processor Health Status"
        >
          <div
            className="health-indicator-dot"
            style={{
              backgroundColor: getStatusColor(),
              animation:
                isLoading && !health ? "pulse 1s ease-in-out infinite" : "none",
            }}
          ></div>
          {health && (
            <span className="health-indicator-text">
              {health.status.charAt(0).toUpperCase()}
            </span>
          )}
        </button>
        <div className="health-indicator-tooltip">{getStatusTooltip()}</div>
      </div>

      <HealthDetailsModal
        health={health}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
      />
    </>
  );
};
